import React, { useEffect, useRef, useState } from 'react';

export default function CancelarPedidoModal({
  abierto,
  pedido,
  procesando,
  onClose,
  onConfirm,
}) {
  const motivoRef = useRef(null);
  const pinRef = useRef(null);

  const [pin, setPin] = useState('');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!abierto) return;

    setPin('');
    setMotivo('');
    setError('');

    requestAnimationFrame(() => {
      motivoRef.current?.focus();
    });
  }, [abierto, pedido?.id]);

  if (!abierto || !pedido) return null;

  // En cocina la comida ya se empezó a preparar: el stock no vuelve.
  const enCocina = ['EN_PREPARACION', 'LISTO'].includes(pedido.estado);

  const confirmar = async (event) => {
    event.preventDefault();

    if (procesando) return;

    if (!motivo.trim()) {
      setError('Indicá el motivo de la cancelación.');
      motivoRef.current?.focus();
      return;
    }

    if (!pin.trim()) {
      setError('Ingresá el PIN de administrador.');
      pinRef.current?.focus();
      return;
    }

    try {
      setError('');

      // El backend valida el PIN y exige rol administrador.
      const resultado = await onConfirm({
        pedidoId: pedido.id,
        autorizacionPin: pin.trim(),
        motivo: motivo.trim(),
      });

      if (!resultado?.ok) {
        setError(
          resultado?.error ||
            'No se pudo cancelar el pedido.'
        );
        setPin('');
        pinRef.current?.focus();
      }
    } catch (err) {
      console.error(
        'Error cancelando pedido Restaurante:',
        err
      );

      setError(
        err?.response?.data?.error ||
          err?.message ||
          'No se pudo cancelar el pedido.'
      );
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-azul-oscuro/85 p-4 backdrop-blur-sm">
      <form
        onSubmit={confirmar}
        className="w-full max-w-lg overflow-hidden rounded-3xl border border-blanco-puro/20 bg-azul-oscuro shadow-2xl"
      >
        <div className="border-b border-blanco-puro/10 px-6 py-5">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-red-300">
            Operación sensible
          </div>

          <h2 className="mt-1 text-2xl font-black text-blanco-puro">
            Cancelar pedido #{pedido.id}
          </h2>

          <p className="mt-2 text-sm leading-6 text-blanco-puro/60">
            {enCocina
              ? 'El pedido se descuenta de la cuenta. Como cocina ya empezó a prepararlo, los productos NO vuelven al stock y queda registrado como pérdida.'
              : 'El pedido se descuenta de la cuenta y los productos vuelven automáticamente al stock.'}
          </p>
        </div>

        <div className="space-y-5 p-6">
          <ul className="space-y-1 rounded-xl border border-blanco-puro/10 bg-blanco-puro/5 px-4 py-3 text-sm text-blanco-puro/80">
            {(pedido.items || []).map((item) => (
              <li key={item.id}>
                {item.cantidad} × {item.producto_nombre}
              </li>
            ))}
          </ul>

          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-blanco-puro/60">
              Motivo
            </span>

            <textarea
              ref={motivoRef}
              value={motivo}
              onChange={(event) =>
                setMotivo(event.target.value)
              }
              disabled={procesando}
              rows={3}
              maxLength={255}
              placeholder="Ej: Pedido cargado en la mesa equivocada"
              className="w-full resize-none rounded-xl border border-blanco-puro/20 bg-azul-oscuro/70 px-4 py-3 text-sm text-blanco-puro placeholder:text-blanco-puro/40 outline-none transition focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/30 disabled:opacity-50"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-blanco-puro/60">
              PIN de administrador
            </span>

            <input
              ref={pinRef}
              name="restaurante-cancelacion-pin"
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              value={pin}
              onChange={(event) =>
                setPin(event.target.value)
              }
              disabled={procesando}
              className="h-12 w-full rounded-xl border border-blanco-puro/20 bg-azul-oscuro/70 px-4 text-center text-xl font-bold tracking-[0.35em] text-blanco-puro outline-none transition focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/30 disabled:opacity-50"
            />
          </label>

          {error && (
            <div className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-100">
              {error}
            </div>
          )}

          <div className="rounded-xl border border-amarillo-dorado/20 bg-amarillo-dorado/10 px-4 py-3 text-xs leading-5 text-blanco-puro/70">
            El pedido no se borra: queda registrado como
            cancelado, con el motivo y quién lo autorizó.
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={procesando}
              className="rounded-xl border border-blanco-puro/20 px-5 py-2.5 text-sm font-bold text-blanco-puro transition hover:bg-blanco-puro/10 disabled:opacity-50"
            >
              Volver
            </button>

            <button
              type="submit"
              disabled={procesando}
              className="rounded-xl bg-red-500 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-red-600 disabled:opacity-50"
            >
              {procesando
                ? 'Cancelando...'
                : 'Cancelar pedido'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
